class YeadayHomeRail extends HTMLElement {
  connectedCallback() {
    this.cleanup?.();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.track = this.querySelector('[data-track]');
    if (!this.track) return;
    const previous = this.querySelector('[data-prev]');
    const next = this.querySelector('[data-next]');
    const dots = this.querySelector('[data-dots]');
    const slides = [...this.track.children].filter(el => el.getBoundingClientRect().width);
    const move = direction => {
      const first = slides[0];
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const maximum = this.track.scrollWidth - this.track.clientWidth;
      const step = (first?.getBoundingClientRect().width || this.track.clientWidth) + gap;
      const target = direction < 0 && this.track.scrollLeft <= 1 ? maximum : direction > 0 && this.track.scrollLeft >= maximum - 1 ? 0 : this.track.scrollLeft + direction * step;
      this.track.scrollTo({ left: target, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    };
    previous?.addEventListener('click', () => move(-1), { signal });
    next?.addEventListener('click', () => move(1), { signal });
    this.track.addEventListener('keydown', event => {
      if (event.target !== this.track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault(); move(event.key === 'ArrowRight' ? 1 : -1);
    }, { signal });
    if (dots) {
      dots.replaceChildren();
      slides.forEach((slide, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.setAttribute('aria-label', `${index + 1} / ${slides.length}`);
        button.addEventListener('click', () => this.track.scrollTo({ left: slide.offsetLeft - slides[0].offsetLeft, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' }), { signal });
        dots.append(button);
      });
    }
    const update = () => {
      if (this.classList.contains('yh-brands__rail')) this.style.setProperty('--brand-scale', this.track.clientWidth / 1280);
      // Fit collection category cards to the available rail without changing other carousels.
      const browse = this.classList.contains('yc-browse__carousel') && !this.closest('.yc-category-directory');
      if (browse) {
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        const fits = matchMedia('(min-width: 750px)').matches && slides.length > 0 &&
          slides.length * 226.505 + Math.max(0, slides.length - 1) * gap <= this.track.clientWidth + 1;
        this.classList.toggle('yc-browse__carousel--fits', fits);
      }
      const max = this.track.scrollWidth - this.track.clientWidth;
      if (browse) {
        if (previous) previous.hidden = max <= 1;
        if (next) next.hidden = max <= 1;
        this.track.tabIndex = max <= 1 ? -1 : 0;
      }
      const left = Math.max(0, Math.min(max, this.track.scrollLeft));
      if (previous) previous.disabled = max <= 1;
      if (next) next.disabled = max <= 1;
      const ratio = this.track.clientWidth / Math.max(1, this.track.scrollWidth);
      this.style.setProperty('--thumb-width', `${Math.max(.1, ratio) * 100}%`);
      this.style.setProperty('--thumb-offset', `${left / Math.max(1, max) * (1 - ratio) * 100}%`);
      const progress = this.querySelector('.yh-progress');
      if (progress) progress.hidden = max <= 1;
      if (dots && slides.length) {
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        const index = Math.round(left / (slides[0].getBoundingClientRect().width + gap));
        [...dots.children].forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
      }
    };
    this.track.addEventListener('scroll', update, { signal, passive: true });
    this.resize = new ResizeObserver(update); this.resize.observe(this.track);
    update();
    this.cleanup = () => { this.controller.abort(); this.resize.disconnect(); };
  }
  disconnectedCallback() { this.cleanup?.(); }
}
if (!customElements.get('yeaday-home-rail')) customElements.define('yeaday-home-rail', YeadayHomeRail);
function scaleHero() {
  document.querySelectorAll('.yh-hero').forEach(hero => hero.style.setProperty('--hero-scale', hero.clientWidth / (hero.clientWidth < 750 ? 440 : 1728)));
}
scaleHero();
window.addEventListener('resize', scaleHero, { passive: true });
document.addEventListener('shopify:section:load', scaleHero);

// Preserve the store's review content while presenting the approved responsive carousel.
class YeadayReviewCarousel extends HTMLElement {
  connectedCallback() {
    if (this.ready || this.sourceObserver) return;
    if (this.hasAttribute('data-native-source')) {
      this.source = this.closest('[data-review-module]')?.querySelector('[data-review-source]');
      if (!this.source) return;
      let currentTitles = null;
      if (this.hasAttribute('data-current-product-titles')) {
        try { currentTitles = new Set(JSON.parse(this.dataset.currentProductTitles)); }
        catch { currentTitles = new Set(); }
      }
      const refresh = () => {
        const seen = new Set();
        const reviews = [...this.source.querySelectorAll('.jdgm-cards-carousel .jdgm-card[data-review-id]')].filter(review => {
          const id = review.dataset.reviewId;
          if (!id || seen.has(id) || !review.querySelector('.jdgm-text')?.textContent.trim()) return false;
          const productTitle = review.querySelector('.jdgm-product-name')?.textContent.trim();
          // Current collection can include stale app records. Only current Shopify titles qualify.
          if (currentTitles && !currentTitles.has(productTitle)) return false;
          seen.add(id);
          return true;
        });
        const signature = reviews.map(review => `${review.dataset.reviewId}:${review.textContent}`).join('|');
        if (signature === this.sourceSignature) return;
        this.sourceSignature = signature;
        this.closest('.yh-reviews').hidden = reviews.length === 0;
        this.renderReviews(reviews);
      };
      this.sourceObserver = new MutationObserver(refresh);
      this.sourceObserver.observe(this.source, { childList: true, subtree: true, characterData: true });
      refresh();
      return;
    }
    const reviews = [...(this.querySelector('template')?.content.querySelectorAll('.jdgm-carousel-item') || [])];
    if (reviews.length) this.renderReviews(reviews);
  }
  renderReviews(reviews) {
    this.abort?.abort();
    this.resize?.disconnect();
    this.querySelector('[data-review-slides]')?.replaceChildren();
    this.querySelector('[data-dots]')?.replaceChildren();
    if (!reviews.length) return;
    this.ready = true;
    this.abort = new AbortController();
    const { signal } = this.abort;
    const host = this.querySelector('[data-review-slides]');
    const dots = this.querySelector('[data-dots]');
    host.tabIndex = 0;
    host.setAttribute('role', 'region');
    host.setAttribute('aria-label', this.closest('.yh-reviews').querySelector('h2').textContent);
    const slides = reviews.map((review, i) => {
      const text = selector => review.querySelector(selector)?.textContent.trim() || '';
      const figure = document.createElement('figure');
      figure.className = 'yh-review-single';
      figure.setAttribute('role', 'group');
      figure.setAttribute('aria-label', `${i + 1} / ${reviews.length}`);
      const caption = document.createElement('figcaption');
      const avatar = document.createElement('span');
      avatar.className = 'yh-review__initials';
      avatar.setAttribute('aria-hidden', 'true');
      const name = text('.jdgm-carousel-item__reviewer-name, .jdgm-reviewer-name');
      avatar.textContent = name.split(/\s+/).filter(Boolean).slice(0, 2).map(part => [...part][0]).join('').toUpperCase();
      const identity = document.createElement('span');
      const author = document.createElement('strong');
      author.textContent = name;
      const label = document.createElement('span');
      const importedIds = new Set((this.dataset.importedReviewIds || '').split(/[\s,]+/).filter(Boolean));
      label.textContent = importedIds.has(review.dataset.reviewId)
        ? this.dataset.importedLabel || ''
        : this.dataset.customerLabel;
      identity.append(author);
      if (label.textContent) identity.append(label);
      caption.append(avatar, identity);
      const quote = document.createElement('blockquote');
      quote.textContent = text('.jdgm-carousel-item__review-body, .jdgm-text');
      const product = document.createElement('p');
      product.className = 'yh-review__product';
      product.textContent = text('.jdgm-carousel-item__product-title, .jdgm-product-name');
      if (this.dataset.presentation === 'collection') {
        figure.classList.add('yc-review-card');
        const rating = review.querySelector('.jdgm-carousel-item__review-rating, .jdgm-stars');
        const stars = document.createElement('div');
        stars.className = 'yc-review-stars';
        stars.setAttribute('role', 'img');
        stars.setAttribute('aria-label', rating?.getAttribute('aria-label') || `${rating?.querySelectorAll('.jdgm--on').length || 0} out of 5 stars`);
        stars.textContent = [...(rating?.children || [])].map(star => star.classList.contains('jdgm--on') ? '★' : '☆').join('');
        const footer = document.createElement('div');
        footer.className = 'yc-review-footer';
        const attribution = document.createElement('div');
        attribution.className = 'yc-review-attribution';
        attribution.append(author.cloneNode(true));
        if (label.textContent) attribution.append(label.cloneNode(true));
        footer.append(attribution, product);
        figure.append(stars, caption, quote, footer);
      } else figure.append(caption, quote, product);
      host.append(figure);
      return figure;
    });
    let index = 0;
    const update = () => {
      const step = Math.max(1, slides[1] ? slides[1].offsetLeft - slides[0].offsetLeft : host.clientWidth);
      index = Math.max(0, Math.min(slides.length - 1, Math.round(host.scrollLeft / step)));
      const firstDot = Math.max(0, Math.min(index - 2, slides.length - 5));
      [...dots.children].forEach((dot, i) => {
        dot.hidden = i < firstDot || i >= firstDot + 5;
        dot.setAttribute('aria-current', String(i === index));
      });
      host.style.height = `${slides[index].getBoundingClientRect().height}px`;
    };
    const show = i => {
      const target = (i + slides.length) % slides.length;
      host.scrollTo({ left: slides[target].offsetLeft - slides[0].offsetLeft, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    };
    slides.forEach((_, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.setAttribute('aria-label', `${i + 1} / ${slides.length}`);
      dot.addEventListener('click', () => show(i), { signal });
      dots.append(dot);
    });
    this.querySelector('[data-prev]').addEventListener('click', () => show(index - 1), { signal });
    this.querySelector('[data-next]').addEventListener('click', () => show(index + 1), { signal });
    host.addEventListener('scroll', update, { signal, passive: true });
    host.addEventListener('keydown', event => {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      show(index + (event.key === 'ArrowRight' ? 1 : -1));
    }, { signal });
    this.resize = new ResizeObserver(update);
    slides.forEach(slide => this.resize.observe(slide));
    this.querySelector('.yh-review-controls').hidden = slides.length < 2;
    update();
  }
  disconnectedCallback() {
    this.sourceObserver?.disconnect();
    this.sourceObserver = null;
    this.sourceSignature = null;
    this.abort?.abort();
    this.resize?.disconnect();
    this.ready = false;
    this.querySelector('[data-review-slides]')?.replaceChildren();
    this.querySelector('[data-dots]')?.replaceChildren();
  }
}
if (!customElements.get('yeaday-review-carousel')) customElements.define('yeaday-review-carousel', YeadayReviewCarousel);
